import type { LicenseType } from '../api';

/**
 * Formata data no padrão pt-BR. Cuidado com timezone:
 *
 * Uma string `YYYY-MM-DD` (formato date-only vindo do backend, ex.:
 * warranty_expires_at, license.expiresAt) é interpretada pelo JS como
 * meia-noite UTC. Em BRT (UTC-3) isso vira 21:00 do dia anterior, e o
 * toLocaleDateString mostra 1 dia a menos.
 *
 * Corrigimos parseando date-only como data LOCAL. Strings com hora ou
 * offset (ex.: 2026-01-15T10:00:00Z, createdAt) continuam interpretadas
 * normalmente — o timezone delas é explícito.
 */
export function formatarData(value?: string | Date | null): string {
  if (!value) return '—';
  let d: Date;
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, day] = value.split('-').map(Number);
      d = new Date(y, m - 1, day);
    } else {
      d = new Date(value);
    }
  } else {
    d = value;
  }
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatarMoeda(value?: string | number | null): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
}

export function formatarTamanho(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function garantiaVencendo(dateStr?: string | null, diasLimite = 90): boolean {
  if (!dateStr) return false;
  // Mesmo cuidado com date-only do formatarData.
  let d: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, day] = dateStr.split('-').map(Number);
    d = new Date(y, m - 1, day);
  } else {
    d = new Date(dateStr);
  }
  if (Number.isNaN(d.getTime())) return false;
  const diff = d.getTime() - Date.now();
  return diff > 0 && diff <= diasLimite * 24 * 60 * 60 * 1000;
}

/**
 * Origem → destino de uma movimentação (issue #300). Quando não há mudança
 * (ou só um dos lados está preenchido), mostra o valor único.
 */
export function formatarOrigemDestino(
  origem?: string | null,
  destino?: string | null,
): string {
  const de = origem?.trim();
  const para = destino?.trim();
  if (de && para && de !== para) return `${de} → ${para}`;
  return de || para || '—';
}

export type SituacaoLicenca = 'vigente' | 'vencendo' | 'vencida' | 'perpetua';

/**
 * Situação da licença pelo vencimento (issue #464): licença não tem
 * situação própria como o ativo. "Vencendo" = vence em até 30 dias,
 * mesmo marco do alerta crítico. Compara datas civis locais.
 */
export function situacaoLicenca(expiresAt: string | null | undefined, now: Date = new Date()): SituacaoLicenca {
  if (!expiresAt) return 'perpetua';
  const [y, m, d] = expiresAt.slice(0, 10).split('-').map(Number);
  const expires = Date.UTC(y, m - 1, d);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((expires - today) / 86_400_000);
  if (days < 0) return 'vencida';
  if (days <= 30) return 'vencendo';
  return 'vigente';
}

export const LICENSE_TYPE_LABELS: Record<LicenseType, string> = {
  microsoft: 'Microsoft',
  adobe: 'Adobe',
  atlassian: 'Atlassian',
  antivirus: 'Antivírus',
  specialized: 'Especializada',
  saas: 'SaaS',
};
